import { ErrorBoundary } from "@sentry/react";
import { MdChecklistRtl } from "react-icons/md";
import { RemoveScroll } from "react-remove-scroll";
import { useCallback, useEffect, useState } from "react";
import { FaUserGroup, FaWhatsapp } from "react-icons/fa6";
import { useMutationState, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  NavLink,
  useNavigate,
  Link,
  useLocation,
  useBeforeUnload,
} from "react-router-dom";

import type { CallDto } from "~/data/call/call.dto";

import { socket } from "~/data/core/socket";
import Logo from "~/ui/shared/assets/logo.png";
import { logout } from "~/data/user/user.service";

import { VideoCall } from "../call/video-call";
import { ExamIcon } from "../shared/icons/exam";
import { MenuIcon } from "../shared/icons/menu";
import { UserAvatar } from "../shared/user-avatar";
import { LaunchIcon } from "../shared/icons/launch";
import { IncomingCall } from "../call/incoming-call";
import { useUser } from "../shared/queries/user-query";
import { ErrorFallback } from "../shared/error-fallback";
import { DashboardIcon } from "../shared/icons/dashboard";
import { useConfig } from "../shared/queries/config-query";
import { CallContext } from "../shared/contexts/call-context";

export function AppLayout() {
  const user = useUser();
  const config = useConfig();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const onLogout = () => {
    logout();
    navigate("/entrar");
    requestAnimationFrame(() => queryClient.clear());
  };

  useEffect(() => socket.subscribe(socket.channels.chats()), []);
  useEffect(
    () => socket.subscribe(socket.channels.chats(`chat_${user.id}`)),
    [user.id]
  );

  const [call, setCall] = useState<CallDto | null>(null);
  const [incomingCall, setIncomingCall] = useState<CallDto | null>(null);
  const [selectingNext, setSelectingNext] = useState(false);
  const closeIncomingCall = useCallback(() => setIncomingCall(null), []);
  const createdCalls = useMutationState({
    filters: { mutationKey: ["call_user"] },
    select: (mutation) => mutation.state.data as CallDto,
  });
  const [createdCall] = [...createdCalls].reverse();
  useEffect(() => {
    // Keep existing state
    if (createdCall) setCall((state) => state ?? createdCall);
  }, [createdCall]);

  const connectingCallId = call?.id || incomingCall?.id;
  useEffect(() => {
    if (!connectingCallId) return;
    return socket.subscribe(socket.channels.video(connectingCallId));
  }, [connectingCallId]);

  useBeforeUnload((e) => {
    if (!call) return;

    // Alert user if try to refresh while in an active call
    e.preventDefault();
    e.returnValue = "";
  });
  useEffect(() => {
    if (!call) return;

    const controller = new AbortController();
    window.addEventListener(
      "unload",
      () =>
        socket.send({
          command: "message",
          identifier: socket.channels.video(call.id),
          data: {
            action: "signal",
            unload: true,
          },
        }),
      controller
    );
    return () => controller.abort();
  }, [call]);

  useEffect(() => socket.on("chat_room_created", setIncomingCall), []);
  useEffect(
    () =>
      socket.on("signal", (data) => {
        if (data.from && data.from === user.id) return; // ignore event sent by myself
        setSelectingNext((curr) => data.selecting_next ?? curr);
      }),
    [user.id]
  );
  useEffect(
    () =>
      socket.on("signal", (data) => {
        if (data.rejected) setCall(null); // close call if peer rejected
      }),
    [user.id]
  );
  useEffect(
    () =>
      socket.on("finish_video_chat", () => {
        setTimeout(() => {
          setCall(null);
          setIncomingCall(null);
          setSelectingNext(false);
        }, 1000);
      }),
    []
  );

  const baseLinks = [
    {
      to: "/",
      label: "Dashboard",
      icon: <DashboardIcon />,
      activeClassName: "text-alternative-1",
    },
    {
      to: "/colegas",
      label: "Colegas",
      icon: <FaUserGroup size={28} />,
      activeClassName: "text-secondary-1",
    },
    {
      to: "/historico",
      label: "Histórico",
      icon: <MenuIcon />,
      activeClassName: "text-secondary-2",
    },
    {
      to: "/cenarios",
      label: "Cenários",
      icon: <ExamIcon />,
      activeClassName: "text-alternative-2",
    },
  ];
  const adminLinks = [
    {
      to: "/checklists",
      label: "Checklists",
      icon: <MdChecklistRtl size={28} />,
      activeClassName: "text-primary-2",
    },
  ];

  return (
    <div className="relative flex h-full">
      <aside className="w-24 bg-white flex flex-col items-center py-6 fixed h-full z-10">
        <Link to="/">
          <img src={Logo} alt="Logotipo" className="h-11" />
        </Link>

        <div className="flex flex-col items-center space-y-6 mt-8 mb-full">
          {[...baseLinks, ...(user.admin ? adminLinks : [])].map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              aria-label={link.label}
              className={({ isActive }) =>
                isActive
                  ? link.activeClassName +
                    " transition-colors duration-200 transform scale-105"
                  : "text-black transform transition-transform duration-200 hover:scale-105"
              }
            >
              {link.icon}
            </NavLink>
          ))}
        </div>

        <div className="mt-auto flex flex-col items-center space-y-4 md:space-y-6">
          <a
            aria-label="Grupo WhatsApp"
            title="Grupo WhatsApp"
            target="_blank"
            rel="noreferer noopener"
            href={config?.whatsapp_link || "#"}
            className="text-green-500"
          >
            <FaWhatsapp size={32} />
          </a>
          <NavLink
            to="/perfil"
            aria-label="Perfil do usuário"
            className={(state) =>
              state.isActive ? "rounded-full border-2 border-secondary-1" : ""
            }
          >
            <UserAvatar size={40} url={user.avatar_url} name={user.name} />
          </NavLink>
          <button
            type="button"
            aria-label="Sair"
            className="text-black"
            onClick={onLogout}
          >
            <LaunchIcon />
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-4">V.1.2</p>
      </aside>

      <main className="flex-1 ml-24">
        <CallContext.Provider value={call}>
          <ErrorBoundary
            key={useLocation().key} // resets on change location
            fallback={(state) => <ErrorFallback error={state.error} />}
          >
            <Outlet />
          </ErrorBoundary>
        </CallContext.Provider>
      </main>

      {selectingNext && (
        <RemoveScroll>
          <div className="fixed inset-0 z-20 grid place-content-center bg-white/50 backdrop-blur-sm">
            <h1 className="text-6xl leading-tight font-alternative font-medium text-center">
              Por favor, aguarde...
            </h1>
            <p className="text-3xl text-primary-1 font-alternative font-semibold">
              Seu colega está selecionando a próxima prática
            </p>
          </div>
        </RemoveScroll>
      )}

      {!!call && (
        <VideoCall
          call={call}
          onCallFinish={() => {
            setCall(null);
            setSelectingNext(false);
          }}
          onTestStart={(data) =>
            navigate(`/praticas/${data.id}?code=${data.join_station_code}`)
          }
        />
      )}

      {!!incomingCall && (
        <IncomingCall
          call={incomingCall}
          onAccept={async () => {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const hasAudio = devices.find((it) => it.kind === "audioinput");
            const hasVideo = devices.find((it) => it.kind === "videoinput");

            // Audio input is required, so we early return here
            if (!hasAudio) {
              alert(
                "É necessário ter um dispositivo de áudio conectado para participar da chamada!"
              );
              return;
            }

            // Video input is recommended but not required
            if (!hasVideo) {
              alert(
                "É recomendado ter um dispositivo de vídeo conectado para participar da chamada."
              );
            }

            setCall(incomingCall);
            closeIncomingCall();
          }}
          onReject={() => {
            socket.send({
              command: "message",
              identifier: socket.channels.video(incomingCall.id),
              data: { action: "signal", rejected: true },
            });
            closeIncomingCall();
          }}
        />
      )}
    </div>
  );
}
